import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectplanService } from '../../services/projectplan.service';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ProjectPlanFolderModel, ProjectPlanModel, PlanGroup } from '../../models/projectplan.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Toast } from "primeng/toast";
import { CommonModule } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { environment } from '../../../environments/environment';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { ProjectModel } from '../../models/project.model';
import { buildProjectAssetDownloadFileName } from '../../utils/download-file-name.utils';
import { MediaFallbackDirective } from '../../shared/directives/media-fallback.directive';

@Component({
  selector: 'app-plans',
  imports: [MenuModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    Toast,
    CommonModule,
    PanelModule,
    MediaFallbackDirective],
  templateUrl: './plans.html',
  styleUrl: './plans.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlansComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  showAddPlanFolderDialog: boolean = false;
  showAddPlanDialog: boolean = false;
  showEditPlanDialog: boolean = false;
  showDeleteConfirmDialog: boolean = false;
  projectId: number = 0;
  folderList: ProjectPlanFolderModel[] = [];
  filteredFolderList: ProjectPlanFolderModel[] = [];
  selectedFiles: any[] = [];
  selectedPlanIds: Set<number> = new Set();
  projectPlanFolderModel: ProjectPlanFolderModel = {} as ProjectPlanFolderModel;
  projectPlanModel: ProjectPlanModel = {} as ProjectPlanModel;
  editingPlan: ProjectPlanModel = {} as ProjectPlanModel;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;
  private dataLoaded = false;
  currentProject: ProjectModel | null = null;

  // Track collapsed state for each folder
  collapsedFolders: Set<number> = new Set();

  get hasSelection(): boolean {
    return this.selectedPlanIds.size > 0;
  }

  get selectedCount(): number {
    return this.selectedPlanIds.size;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ProjectplanService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService
  ) { }

  ngOnInit(): void {
    this.projectStateService.currentProject$.pipe(takeUntil(this.destroy$)).subscribe(project => {
      this.currentProject = project;
      this.cdr.markForCheck();
    });

    this.route.paramMap.subscribe(params => {
      const projectIdParam = params.get('projectId');
      if (projectIdParam) {
        this.projectId = parseInt(projectIdParam);
        if (this.projectId > 0) {
          this.tryLoadPlans();
        } else {
          console.error('Invalid project ID:', this.projectId);
        }
      } else {
        console.error('No project ID found in URL parameters');
      }
    });

    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.applySearch(search);
    });

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.PLANS, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.PLANS, PERMISSION_KEYS.CREATE);
      this.canUpdate = this.permissionHelper.has(PAGE_KEYS.PLANS, PERMISSION_KEYS.UPDATE);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.PLANS, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadPlans();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredFolderList = [...this.folderList];
    } else {
      this.filteredFolderList = this.folderList.filter(folder =>
        (folder.folderName || '').toLowerCase().includes(term) ||
        (folder.projectPlanList || []).some(p =>
          (p.planName || p.planFullName || '').toLowerCase().includes(term)
        )
      );
    }
    this.cdr.markForCheck();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canUpdate && !this.canDelete);
    this.cdr.markForCheck();
  }

  private tryLoadPlans(): void {
    if (this.dataLoaded || !this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.getProjectPlans();
    this.dataLoaded = true;
  }

  getProjectPlans(): void {
    this.service.getProjectPlans(this.projectId).subscribe({
      next: (folders: ProjectPlanFolderModel[]) => {
        this.folderList = folders;
        this.applySearch(this.projectStateService.getPageSearch());

        folders.forEach(folder => {
          if (!folder.projectPlanList || folder.projectPlanList.length === 0) {
            this.collapsedFolders.add(folder.planFolderId);
          }

          folder.projectPlanList?.forEach(plan => {
            plan.isLoading = true;
            if (plan.planThumbnailPath) {
              this.loadThumbnail(plan);
            } else {
              plan.isLoading = false;
            }
          });
        });

        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error fetching project plans:', error);
        this.cdr.markForCheck();
      }
    });
  }

  groupPlansByName(planList: ProjectPlanModel[]): PlanGroup[] {
    const grouped = new Map<string, ProjectPlanModel[]>();

    planList.forEach(plan => {
      const existing = grouped.get(plan.planName || '') || [];
      existing.push(plan);
      grouped.set(plan.planName || '', existing);
    });

    return Array.from(grouped.entries()).map(([planName, pages]) => {
      const sortedPages = pages.sort((a, b) => {
        const aMatch = a.planFullName?.match(/Page_(\d+)/i);
        const bMatch = b.planFullName?.match(/Page_(\d+)/i);
        const aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
        const bNum = bMatch ? parseInt(bMatch[1], 10) : 0;
        return aNum - bNum;
      });

      return {
        planName,
        pages: sortedPages,
        isMultiPage: pages.length > 1,
      };
    });
  }

  loadThumbnail(plan: ProjectPlanModel): void {
    if (!plan.planThumbnailPath) return;
    plan.thumbnailUrl = `${environment.cdnBaseUrl}?key=${encodeURIComponent(plan.planThumbnailPath)}`;
    plan.isLoading = false;
    this.cdr.markForCheck();
  }

  getDownloadMenuItems(plan: ProjectPlanModel) {
    return [
      {
        label: 'Original File',
        icon: 'pi pi-file',
        command: () => this.downloadOriginalFile(plan)
      },
      {
        label: 'Optimized File',
        icon: 'pi pi-image',
        command: () => this.downloadOptimizedFile(plan)
      }
    ];
  }

  downloadOriginalFile(plan: ProjectPlanModel): void {
    if (!plan.planId) return;

    this.service.downloadProjectPlan(plan.planId, false).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(this.currentProject, 'Plan', plan.planFullName || 'plan', {
          variant: 'Original',
          fallbackExtension: 'pdf',
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading original file:', err);
      }
    });
  }

  downloadOptimizedFile(plan: ProjectPlanModel): void {
    if (!plan.planId) return;

    this.service.downloadProjectPlan(plan.planId, true).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(this.currentProject, 'Plan', plan.planFullName || 'plan', {
          variant: 'Optimized',
          fallbackExtension: 'pdf',
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading optimized file:', err);
      }
    });
  }

  addPlanFolder() {
    if (!this.canCreate || this.isProjectInactive) return;
    const folderName = this.projectPlanFolderModel.folderName?.trim();
    if (!folderName) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name cannot be empty.' });
      return;
    }
    const duplicate = this.folderList.some((f: ProjectPlanFolderModel) =>
      f.folderName?.trim().toLowerCase() === folderName.toLowerCase()
    );
    if (duplicate) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Folder name already exists.' });
      return;
    }
    this.projectPlanFolderModel.projectId = this.projectId;
    this.service.addProjectPlanFolder(this.projectPlanFolderModel).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Plan folder added successfully.' });
        this.showAddPlanFolderDialog = false;
        this.getProjectPlans();
        this.projectPlanFolderModel = {} as ProjectPlanFolderModel;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add plan folder.' });
      }
    });
  }

  selectedFolder: ProjectPlanFolderModel | null = null;
  onAddPlanToFolder(folder: ProjectPlanFolderModel) {
    if (!this.canCreate || this.isProjectInactive) return;
    this.selectedFolder = folder;
    this.projectPlanModel.planFolderId = folder.planFolderId;
    this.showAddPlanDialog = true;
  }

  addProjectPlan() {
    if (!this.canCreate || this.isProjectInactive) return;
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'No files selected.' });
      return;
    }
    const formData = new FormData();
    formData.append('projectPlanData', JSON.stringify({
      projectId: this.projectId,
      planFolderId: this.selectedFolder?.planFolderId
    }));
    this.selectedFiles.forEach((file: File) => {
      formData.append('files', file);
    });
    this.service.addProjectPlan(formData).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Plan(s) uploaded successfully.' });
        this.showAddPlanDialog = false;
        this.projectPlanModel = {} as ProjectPlanModel;
        this.selectedFiles = [];
        this.selectedFolder = null;
        this.getProjectPlans();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload plan(s).' });
      }
    });
  }

  onFileSelect(event: any) {
    if (!this.canCreate || this.isProjectInactive) return;
    if (event && event.files) {
      this.selectedFiles = [...this.selectedFiles, ...event.files];
    }
  }

  onFileRemove(event: any) {
    if (!this.canCreate || this.isProjectInactive) return;
    if (event && event.file) {
      this.selectedFiles = this.selectedFiles.filter(f => f !== event.file);
    }
  }

  onFileClear() {
    if (!this.canCreate || this.isProjectInactive) return;
    this.selectedFiles = [];
  }

  trackByPlanId(index: number, plan: any) {
    return plan.planId;
  }

  togglePlanSelection(planId: number, event: Event): void {
    event.stopPropagation();
    if (this.selectedPlanIds.has(planId)) {
      this.selectedPlanIds.delete(planId);
    } else {
      this.selectedPlanIds.add(planId);
    }
    this.cdr.markForCheck();
  }

  isPlanSelected(planId: number): boolean {
    return this.selectedPlanIds.has(planId);
  }

  clearSelection(): void {
    this.selectedPlanIds.clear();
    this.cdr.markForCheck();
  }

  deleteSelected(): void {
    if (!this.canDelete || this.isProjectInactive || !this.hasSelection) return;
    this.showDeleteConfirmDialog = true;
  }

  confirmDelete(): void {
    if (!this.hasSelection) return;
    const idsToDelete = Array.from(this.selectedPlanIds);
    const count = idsToDelete.length;
    const deleteObservables = idsToDelete.map(id => this.service.deleteProjectPlan(id));
    forkJoin(deleteObservables).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `${count} plan(s) deleted successfully.` });
        this.showDeleteConfirmDialog = false;
        this.clearSelection();
        this.getProjectPlans();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete some plans.' });
        this.showDeleteConfirmDialog = false;
      }
    });
  }

  toggleFolderCollapse(folderId: number): void {
    if (this.collapsedFolders.has(folderId)) {
      this.collapsedFolders.delete(folderId);
    } else {
      this.collapsedFolders.add(folderId);
    }
  }

  isFolderCollapsed(folderId: number): boolean {
    return this.collapsedFolders.has(folderId);
  }

  openPlanInViewer(plan: ProjectPlanModel): void {
    const effectiveFolderId = plan.planFolderId ?? 0;

    const folder = this.folderList.find(f => f.planFolderId === effectiveFolderId);
    if (!folder || !folder.projectPlanList) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Folder plans not available.' });
      return;
    }

    this.router.navigate(['/plans', this.projectId, 'view', effectiveFolderId], {
      state: {
        projectPlanList: folder.projectPlanList,
        selectedPlanId: plan.planId
      }
    });
  }

  onEditPlan(plan: ProjectPlanModel): void {
    if (!this.canUpdate || this.isProjectInactive) return;
    this.editingPlan = { ...plan };
    this.showEditPlanDialog = true;
  }

  updatePlan(): void {
    if (!this.canUpdate || this.isProjectInactive) return;
    if (!this.editingPlan.planFullName?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Plan name cannot be empty.' });
      return;
    }

    this.service.updateProjectPlan(this.editingPlan).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Plan updated successfully.' });
        this.showEditPlanDialog = false;
        this.getProjectPlans();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update plan.' });
      }
    });
  }

}
